import React from 'react';
import { Box, Modal, Typography, Button, Stack } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ShareIcon from '@mui/icons-material/Share';
import InboxIcon from '@mui/icons-material/Inbox';

import { useOnboarding } from 'contexts/OnboardingContext';

type OnboardingStep = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

const steps: OnboardingStep[] = [
  {
    title: 'Share Your Link',
    description: 'Tap "Share My Link" to send it to friends, teammates, or any chat.',
    icon: <ShareIcon sx={{ fontSize: 36 }} />
  },
  {
    title: 'Receive Messages',
    description: 'Anonymous messages will arrive in your Inbox. View them anytime by tapping the Inbox tab from the bottom menu.',
    icon: <InboxIcon sx={{ fontSize: 36 }} />
  },
  {
    title: 'Unlock Hints',
    description: 'Tap "Unlock Next Hint" on any message to discover clues about who sent it.',
    icon: <LightbulbIcon sx={{ fontSize: 36 }} />
  }
];

export default function OnboardingModal(): JSX.Element {
  const theme = useTheme();
  const { isOnboarding, currentStep, nextStep, prevStep, endOnboarding } = useOnboarding();

  const handleNext = (): void => {
    if (currentStep < steps.length - 1) {
      nextStep();
    } else {
      endOnboarding();
    }
  };

  const handleSkip = (): void => {
    endOnboarding();
  };

  const step = steps[Math.min(currentStep, steps.length - 1)];
  const isLastStep = currentStep === steps.length - 1;
  const primary = theme.palette.primary.main;
  const surfaceHigh = theme.palette.mode === 'dark' ? '#382427' : theme.palette.background.paper;
  const surfaceHighest = theme.palette.mode === 'dark' ? '#443033' : theme.palette.background.default;
  const outlineVariant = theme.palette.mode === 'dark' ? '#5a4042' : alpha(theme.palette.text.primary, 0.2);

  return (
    <Modal
      open={isOnboarding}
      onClose={handleSkip}
      aria-labelledby="onboarding-title"
      aria-describedby="onboarding-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        backgroundColor: alpha(theme.palette.background.default, 0.8),
        backdropFilter: 'blur(16px)'
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 22, stiffness: 280 }}
        style={{ width: '100%', maxWidth: 384 }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            bgcolor: surfaceHigh,
            borderRadius: 6,
            boxShadow: `0 10px 30px ${alpha(primary, 0.2)}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          {/* Gradient light leaks */}
          <Box
            sx={{
              position: 'absolute',
              top: -96,
              left: -96,
              width: 256,
              height: 256,
              borderRadius: '50%',
              bgcolor: alpha(primary, 0.2),
              filter: 'blur(64px)',
              pointerEvents: 'none'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -96,
              right: -96,
              width: 256,
              height: 256,
              borderRadius: '50%',
              bgcolor: alpha(primary, 0.1),
              filter: 'blur(64px)',
              pointerEvents: 'none'
            }}
          />

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              px: 4,
              pt: 4,
              pb: 5,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Icon Highlight */}
            <Box
              sx={{
                position: 'relative',
                mb: 4,
                width: 96,
                height: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: alpha(primary, 0.1),
                  borderRadius: '50%',
                  filter: 'blur(20px)'
                }}
              />
              <motion.div
                key={currentStep}
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 15, stiffness: 200 }}
                style={{ position: 'relative' }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: surfaceHighest,
                    border: `1px solid ${alpha(primary, 0.2)}`,
                    boxShadow: `0 8px 24px ${alpha(primary, 0.1)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: primary
                  }}
                >
                  {step.icon}
                </Box>
              </motion.div>
            </Box>

            {/* Title */}
            <motion.div
              key={`title-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              style={{ width: '100%' }}
            >
              <Typography
                id="onboarding-title"
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 1.5,
                  letterSpacing: '-0.01em'
                }}
              >
                {step.title}
              </Typography>
            </motion.div>

            {/* Description */}
            <motion.div
              key={`desc-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              style={{ width: '100%', maxWidth: 260 }}
            >
              <Typography
                id="onboarding-description"
                sx={{
                  fontSize: '0.875rem',
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                  mb: 5
                }}
              >
                {step.description}
              </Typography>
            </motion.div>

            {/* Actions */}
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                sx={{
                  width: '100%',
                  py: 1.75,
                  borderRadius: 3,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  boxShadow: `0 10px 25px ${alpha(primary, 0.2)}`,
                  transition: 'all 0.15s',
                  '&:hover': { opacity: 0.9 },
                  '&:active': { transform: 'scale(0.95)' }
                }}
              >
                {isLastStep ? 'Done' : 'Next'}
              </Button>

              {currentStep > 0 && (
                <Button
                  onClick={prevStep}
                  sx={{
                    width: '100%',
                    py: 1.25,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: theme.palette.text.secondary,
                    transition: 'all 0.15s',
                    '&:hover': { backgroundColor: 'transparent', color: primary },
                    '&:active': { transform: 'scale(0.95)' }
                  }}
                >
                  Back
                </Button>
              )}

              {!isLastStep && (
                <Button
                  onClick={handleSkip}
                  sx={{
                    width: '100%',
                    py: 1.25,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: theme.palette.text.secondary,
                    transition: 'all 0.15s',
                    '&:hover': { backgroundColor: 'transparent', color: primary },
                    '&:active': { transform: 'scale(0.95)' }
                  }}
                >
                  Skip Tutorial
                </Button>
              )}
            </Stack>

            {/* Progress Dots */}
            <Box display="flex" gap={1} sx={{ mt: 4 }}>
              {steps.map((_, index) => {
                const active = index === currentStep;
                return (
                  <Box
                    key={index}
                    sx={{
                      width: active ? 12 : 6,
                      height: 6,
                      borderRadius: 9999,
                      bgcolor: active ? primary : outlineVariant,
                      boxShadow: active ? `0 0 8px ${alpha(primary, 0.6)}` : 'none',
                      transition: 'all 0.2s'
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Modal>
  );
}
